import { z } from "zod";
import { ExerciseElement } from "./exercises.ts";
import { HomeworkPhoto } from "./homework-photo.ts";
import {
  HomeworkLinkOpen,
  HomeworkMaterialFileIds,
  HomeworkMaterialImage,
  HomeworkMaterialMedia,
  HomeworkTaskText,
} from "./homework-material.ts";
import { ReturnedAnswerReview } from "./homework-answer-review.ts";
import { UserId } from "./user.ts";

/**
 * Домашняя работа: контракты списка, карточки и обвязки.
 *
 * Проект целиком в `docs/homework-design.md`. Ключевые решения, от которых
 * зависит форма этих контрактов:
 *
 * - домашка это СНИМОК: состав скомпилирован при создании и не меняется никогда
 *   (1.2 проекта). Ручки правки состава не существует, правится только обвязка
 * - хранимого статуса нет, состояние вычисляется из фактов (1.5). Поэтому здесь
 *   лежат и чистые функции вычисления: они одни на список, карточку и ленту
 * - эталоны в этих контрактах не появляются НИКОГДА: у них своя ручка со своими
 *   схемами в `@edu/shared/checking`, чтобы `ExerciseKey` не уехал в стартовый
 *   чанк вместе с баррелем
 */

export const HomeworkId = z.string().uuid();
export type HomeworkId = z.infer<typeof HomeworkId>;

/**
 * Потолок числа заданий. Один на оба источника, из смысла, а не из техники:
 * домашка на 50 заданий это уже не домашка, а контрольная на неделю (7.1
 * проекта). Отказ `homework_full`, а не усечение: молча срезанный список это
 * молчаливая деградация, которую запрещает 5.5 CLAUDE.md.
 */
export const HOMEWORK_MAX_EXERCISES = 50;

/**
 * Рубеж накопления: потолок попыток на (домашка, элемент, ученик, режим).
 *
 * Домашку, в отличие от урока, ничто не завершает, и вкладка, залипшая на
 * ретрае, писала бы 86 400 строк в сутки в пределах лимита частоты. Потолок на
 * два порядка выше любой честной работы: это защита от робота, а не лимит для
 * человека (7.4 проекта).
 */
export const HOMEWORK_ELEMENT_ATTEMPT_MAX = 100;

export const HomeworkTitle = z.string().trim().min(1).max(200);

/**
 * Заготовка как источник заданий: выбранные упражнения материала.
 *
 * `elementIds` это МНОЖЕСТВО, а не порядок: порядок заданий в ленте задаёт
 * обход слайдов заготовки (3.1 проекта). Отсутствие поля означает «все
 * отвечаемые».
 */
export const HomeworkPlanSource = z.object({
  kind: z.literal("plan"),
  planId: z.string().uuid(),
  /**
   * Потолок здесь ТЕХНИЧЕСКИЙ (границы тела), а не смысловой: смысловой это
   * `HOMEWORK_MAX_EXERCISES` с отказом `homework_full`, и он обязан сработать
   * на 51 задании внятным кодом, а не безликим `invalid_body` схемы.
   */
  elementIds: z.array(z.string().min(1).max(200)).max(2000).optional(),
});
export type HomeworkPlanSource = z.infer<typeof HomeworkPlanSource>;

/**
 * Заказ на одну тему: сколько заданий и какого уровня (4.2 фазы 2).
 *
 * ⚠️ `skill` это СТРОКА, а не юнион `SkillSlug`, и причина не та же, что у
 * покрытия. Там свободная строка защищает старый бандл от нового навыка в
 * ответе; здесь она нужна затем, чтобы неизвестный слаг отвечал внятным
 * `generator_unavailable` с перечнем того, что есть, а не безликим
 * `invalid_body` от схемы. Обе причины записаны, чтобы их не перепутали.
 *
 * ⚠️ Тип ПЕРЕИСПОЛЬЗУЕТСЯ английским (`HomeworkEngSource`), и это не небрежность
 * копирования: все три поля предметно-нейтральны. `skill` свободная строка,
 * `count` число заданий, `difficulty` это 1-2-3 у обоих предметов (у английского
 * ось задана разделом 3 `docs/english-generators-design.md`, а не CEFR, ровно
 * затем, чтобы шкала совпала). Имя типа при этом становится неточным, и это
 * долг на отдельный механический коммит, а не работа волны генераторов:
 * переименование тронуло бы десятки файлов и утопило бы в себе настоящий диф.
 */
export const OgeTopicPick = z.object({
  skill: z.string().min(1).max(100),
  /**
   * 1 лёгкий, 2 средний, 3 сложный. На проводе НЕОБЯЗАТЕЛЕН, хотя форма
   * всегда его присылает (1.2 фазы 2): за формой сидит человек, и случайную
   * смесь уровней ему оставлять нельзя, а будущий автомат повторения уровень не
   * выбирает вовсе, потому что за ним человека нет. Это не расхождение схемы с
   * решением, а разделение двух вопросов.
   */
  difficulty: z.number().int().min(1).max(3).optional(),
  /**
   * Техническая граница тела, как у `elementIds`. Смысловые потолка два, и оба
   * отвечают своим кодом: сумма по всем пикам это `homework_full`, потолок по
   * пространству задач темы это `generator_unavailable`.
   */
  count: z.number().int().min(1).max(HOMEWORK_MAX_EXERCISES),
});
export type OgeTopicPick = z.infer<typeof OgeTopicPick>;

/**
 * Генераторы МАТЕМАТИКИ как источник заданий: темы, уровни и количество (3.1
 * фазы 2).
 *
 * ⚠️ Дискриминант `'oge'` не переименовывается никогда, даже когда имя перестанет
 * быть точным. Он не только приезжает в теле создания: тем же словом записан
 * `origin` каждой уже выданной строки `homework_exercise`
 * (`HomeworkOgeOrigin` в `@edu/shared/checking`), и переименование обнулило бы
 * диагностику прошлых работ молча, без единой ошибки компиляции.
 */
export const HomeworkOgeSource = z.object({
  kind: z.literal("oge"),
  picks: z.array(OgeTopicPick).min(1).max(HOMEWORK_MAX_EXERCISES),
});
export type HomeworkOgeSource = z.infer<typeof HomeworkOgeSource>;

/**
 * Генераторы АНГЛИЙСКОГО как источник заданий (раздел 7 пункт 6
 * `docs/english-generators-design.md`). Поля те же, что у математики, и тип
 * пика тот же `OgeTopicPick`: предметно-нейтрален он целиком.
 *
 * ⚠️ Отдельный член юниона, а НЕ поле `subject` внутри существующего, и довод
 * решающий: диагноз по неверному ответу ищется по словарю ошибок предмета
 * (`describeMisconception`), а словарей теперь два. С одним дискриминантом и
 * полем `subject` выбор словаря стал бы догадкой по значению, то есть однажды
 * совпавшие идентификаторы математики и английского дали бы ученику чужой
 * диагноз молча. С двумя членами словарь выбирается ТИПОМ, и забыть ветку не
 * даст компилятор.
 *
 * Ветка чтения при этом стоит не здесь, а у `HomeworkOrigin`: сюда приезжает
 * заказ, туда записывается происхождение. Оба юниона обязаны расширяться парой,
 * иначе заказ соберётся, а диагноз потеряется.
 */
export const HomeworkEngSource = z.object({
  kind: z.literal("eng"),
  picks: z.array(OgeTopicPick).min(1).max(HOMEWORK_MAX_EXERCISES),
});
export type HomeworkEngSource = z.infer<typeof HomeworkEngSource>;

/**
 * Источник заданий: заготовка, генераторы математики либо генераторы
 * английского (1.1 фазы 2). Дискриминированный юнион, а не два поля: домашка из
 * генераторов проходит ТОТ ЖЕ путь, что домашка из заготовки, и разница ровно в
 * том, откуда взялся источник упражнения.
 *
 * ⚠️ Массива `sources` нет: одна работа собирается из одного источника
 * (решение владельца 17.08.2026, разбор в 5.5 и разделе 10 фазы 2). У двух
 * предметов это значит ещё и «одна работа по одному предмету», и смешанной
 * домашки не бывает: `homework_exercise.subject` заполняется веткой источника.
 */
export const HomeworkSource = z.discriminatedUnion("kind", [
  HomeworkPlanSource,
  HomeworkOgeSource,
  HomeworkEngSource,
]);
export type HomeworkSource = z.infer<typeof HomeworkSource>;

export const CreateHomeworkRequest = z.object({
  /**
   * Ключ идемпотентности создания (1.4 docs/homework-phase2-design.md): uuid,
   * который придумывает клиент, один на (нажатие, ученик). Повтор с тем же
   * ключом возвращает ту же работу и код 200, а не создаёт вторую.
   *
   * ⚠️ ОБЯЗАТЕЛЕН с 18.08.2026, шаг А4, contract-половина пары expand-contract
   * (раздел 7 фазы 2). До этого поле было необязательным ровно одну выкатку,
   * чтобы вкладка преподавателя, открытая со старым бандлом, не получила
   * `invalid_body` на «Задать» между уроками.
   *
   * Признак готовности был назван измеримым (сутки без записей «создание без
   * ключа идемпотентности» в логах), но сработал не он: владелец подтвердил
   * 18.08.2026, что стендом кроме него никто не пользуется, то есть старых
   * вкладок нет ни одной. Записей в логах при этом тоже ноль, но ноль от
   * отсутствия старых клиентов и ноль от отсутствия обращений выглядят
   * одинаково, и полагаться тут стоило на первое, а не на второе.
   */
  requestId: z.string().uuid(),
  /**
   * След одной раздачи (1.5 фазы 2): общий uuid у всех работ, созданных одним
   * нажатием. Выдача одному ученику раздачей не является, поэтому необязателен
   * и останется таким.
   */
  batchId: z.string().uuid().optional(),
  studentUserId: UserId,
  title: HomeworkTitle.optional(),
  /** Момент миллисекундами эпохи. Отсутствие означает «без срока». */
  dueAt: z.number().int().nullable().optional(),
  /** Подпись «задана после занятия». Навигация, а не источник прав (1.1). */
  lessonId: z.string().uuid().optional(),
  /**
   * Источник заданий. ⚠️ НЕОБЯЗАТЕЛЕН с 26.09.2026
   * (`docs/homework-scratch-design.md`, 1.4): домашка бывает из одного текста
   * задания и картинок. Колонки `homework.source` и `plan_id` nullable с первой
   * миграции домашки, поэтому это не расширение схемы базы.
   *
   * Отказ `homework_empty` при этом двух видов: источник выбран, а после отсева
   * не осталось ни одного задания (как раньше, даже при картинках), и источника
   * нет вовсе вместе с текстом и картинками.
   */
  source: HomeworkSource.optional(),
  /**
   * Текст задания (1.1 проекта): простой текст до 10 000 знаков, переносы как
   * есть. Живёт своей колонкой, а не в снимке `source`.
   */
  taskText: HomeworkTaskText.optional(),
  /**
   * Картинки материала в порядке показа, файлы медиатеки преподавателя (1.2
   * проекта). Годится только своя картинка, не в корзине, положенная самим
   * преподавателем и не бывшая фото решения; иначе отказ `file_not_found` всей
   * домашке, а не молчаливый отсев.
   */
  materialFileIds: HomeworkMaterialFileIds.optional(),
});
export type CreateHomeworkRequest = z.infer<typeof CreateHomeworkRequest>;

export const UpdateHomeworkRequest = z.object({
  title: HomeworkTitle.optional(),
  /** `null` снимает срок. Отсутствие поля означает «не трогать». */
  dueAt: z.number().int().nullable().optional(),
});
export type UpdateHomeworkRequest = z.infer<typeof UpdateHomeworkRequest>;

/**
 * Счётчики строки списка. Формулы названы в 7.2 проекта, чтобы соседнюю не
 * выбрали случайно: все три по `practice = false` и `voided_at IS NULL`.
 *
 * ⚠️ `solved` это «последняя зачётная попытка верна», НЕ «число верных попыток»
 * (это `correctTotal` ручки попыток) и не «когда-либо решил верно»: показатель
 * обязан совпадать с «верно X из N» раздела 5 и с правилом вердикта в ленте.
 */
export const HomeworkCounters = z.object({
  total: z.number().int().nonnegative(),
  attempted: z.number().int().nonnegative(),
  solved: z.number().int().nonnegative(),
  /**
   * Задания с автопроверкой: знаменатель «верно X из N» (1.3
   * docs/translate-exercise-design.md). У перевода вердикт выносит человек, и
   * деление `solved` на `total` показало бы «верно 0/1» у сделанной работы, а
   * строку видят ученик и родитель. При нуле доли нет вовсе.
   *
   * ⚠️ «Выполнена» по-прежнему считается от `total`, а не от этого числа:
   * ученик свою часть сделал и у перевода (1.4 там же).
   */
  gradable: z.number().int().nonnegative(),
  /**
   * Когда ученик отметил работу сделанной: нажал «Сделал(а)», приложил
   * первое фото (1.5 `docs/homework-scratch-design.md`) или впервые отправил
   * ответ текстом (1.8 там же). ФАКТ, а не вывод из
   * живого списка фото: удаление фото его не снимает, иначе удаление
   * единственного снимка после срока возвращало бы работу в просрочку.
   *
   * На состояние влияет ТОЛЬКО у работы без заданий (`total = 0`); у
   * остальных это просто факт про ученика. Умолчание держит старый сервер.
   */
  studentDoneAt: z.number().int().nullable().default(null),
  /**
   * Сколько фото решения приложено к работе. Едет обеим ролям: ученик видит,
   * что приложил. Число НОВЫХ (непросмотренных) фото едет отдельно и только
   * преподавателю, см. `newPhotos` у `HomeworkSummary`.
   */
  photos: z.number().int().nonnegative().default(0),
  /**
   * Когда ученик последний раз отправил ответ текстом (1.8
   * `docs/homework-scratch-design.md`); `null` означает «ответа нет». Едет
   * обеим ролям: это факт про работу ученика. Переживает стирание текста по
   * сроку. Признак «новый» едет отдельно и только преподавателю, см.
   * `newAnswer` у `HomeworkSummary`. Умолчание держит старый сервер.
   */
  answerAt: z.number().int().nullable().default(null),
});
export type HomeworkCounters = z.infer<typeof HomeworkCounters>;

/**
 * Признак проверки перевода в строке домашки (5.3
 * `docs/translate-review-design.md`). Считается по ПОСЛЕДНЕЙ попытке каждого
 * неаннулированного перевода работы:
 *
 *   awaiting   попытка без проверки или с черновиком: «ждёт проверки»
 *   fix        проверка возвращена, есть «неверно» или «нет ответа»
 *   checked    проверка возвращена, всё верно
 *
 * `fix` и `checked` едут только ученику, преподавателю из трёх только
 * `awaiting` (разбор у `homeworkReviewState`). Отсутствие поля: переводов нет, не отправлен ни один, либо ответ стёрт по
 * сроку без проверки. ⚠️ Это ПРИЗНАК, а не число, и числом не станет: «ждёт
 * проверки: 14» был бы счётом в адрес преподавателя (5.10 CLAUDE.md), поэтому
 * в фильтры списка признак не добавляется вовсе.
 */
export const HomeworkReviewState = z.enum(["awaiting", "fix", "checked"]);
export type HomeworkReviewState = z.infer<typeof HomeworkReviewState>;

/**
 * Признак строки из трёх фактов по работе: есть ли среди её переводов
 * ждущий проверки, возвращённый с ошибками и возвращённый без ошибок.
 *
 * ⚠️ Роли видят РАЗНОЕ (таблица 5.3 проекта). Преподавателю едет только «ждёт
 * проверки»: возвращённая работа у него выглядит как выполненная, и отдельной
 * плашки «есть что исправить» у него нет. Ученику важнее «есть что исправить»:
 * пока один перевод ждёт, второй уже можно исправлять, и «ждёт проверки»
 * спрятало бы это от него.
 */
export function homeworkReviewState(
  flags: { awaiting: boolean; fix: boolean; checked: boolean },
  role: "teacher" | "student",
): HomeworkReviewState | null {
  if (role === "teacher") return flags.awaiting ? "awaiting" : null;
  const order: readonly HomeworkReviewState[] = ["fix", "awaiting", "checked"];
  return order.find((state) => flags[state]) ?? null;
}

export const HomeworkSummary = z.object({
  id: HomeworkId,
  title: z.string(),
  teacherUserId: z.string().uuid(),
  studentUserId: z.string().uuid(),
  /** Имя второй стороны: ученика у преподавателя, преподавателя у ученика. */
  counterpartName: z.string().nullable(),
  myRole: z.enum(["teacher", "student"]),
  createdAt: z.number().int(),
  dueAt: z.number().int().nullable(),
  firstOpenedAt: z.number().int().nullable(),
  revealedAt: z.number().int().nullable(),
  /** Не null означает «в корзине». Ученику такие строки не отдаются вовсе. */
  deletedAt: z.number().int().nullable(),
  lessonId: z.string().uuid().nullable(),
  planId: z.string().uuid().nullable(),
  /**
   * След раздачи, которой работа создана (1.5 фазы 2).
   *
   * ⚠️ Едет ТОЛЬКО роли преподавателя, тем же единственным правилом сборки, что
   * `origin`: ученику он бесполезен, а список идентификаторов чужих раздач это
   * лишнее знание о других учениках. Поэтому поле необязательное: у ученика его
   * нет вовсе, у преподавателя `null` означает «работа задана вне раздачи».
   */
  batchId: z.string().uuid().nullable().optional(),
  counters: HomeworkCounters,
  /**
   * Признак проверки перевода (разбор у `HomeworkReviewState`). Едет обеим
   * ролям; отсутствие означает «признака нет». Необязателен и потому, что
   * новый бандл обязан разбирать ответ старого сервера.
   */
  review: HomeworkReviewState.optional(),
  /**
   * Сколько фото решения преподаватель ещё не открывал крупно (1.5
   * `docs/homework-scratch-design.md`).
   *
   * ⚠️ Едет ТОЛЬКО преподавателю, тем же приёмом, что `review`: у ученика
   * «новых» читается как «преподаватель не смотрел», а это счёт в его адрес
   * (решение владельца 23.08.2026). У ученика поля нет вовсе, а не ноль.
   */
  newPhotos: z.number().int().nonnegative().optional(),
  /**
   * Ответ текстом отправлен после того, как преподаватель последний раз
   * открывал результаты (1.8 `docs/homework-scratch-design.md`).
   *
   * ⚠️ Едет ТОЛЬКО преподавателю, тем же гейтом, что `newPhotos`, и по той же
   * причине: у ученика «новый» читается как «преподаватель не смотрел».
   */
  newAnswer: z.boolean().optional(),
});
export type HomeworkSummary = z.infer<typeof HomeworkSummary>;

/**
 * Размер страницы списка, выбранный против отсечки 16 КБ, а не по ощущению
 * скорости (4.3 фазы 2).
 *
 * ⚠️ В режиме белого списка отдача обрывается около 16 КБ, и это режим по
 * умолчанию у большинства мобильных сессий (раздел 3 CLAUDE.md). Обрыв даёт не
 * укороченный список, а ошибку разбора JSON, то есть пустой экран. Строка
 * сводки это около 620 байт сырого JSON, из них треть это uuid, которые не
 * сжимаются: 25 строк это ≈15 КБ сырых и 5-6 КБ сжатых, 50 строк это ≈31 КБ
 * сырых и уже граница. Прежний потолок в 200 строк это ≈124 КБ, то есть
 * гарантированный обрыв.
 *
 * `limit` вне границ это отказ `invalid_query`, а не молчаливое усечение.
 */
export const HOMEWORK_PAGE_LIMIT_DEFAULT = 25;
export const HOMEWORK_PAGE_LIMIT_MAX = 50;

/**
 * Фильтр состояния списка (5.2 фазы 2). ⚠️ Считается СЕРВЕРОМ, и это не
 * оптимизация: клиентский фильтр поверх курсорной страницы систематически пуст
 * ровно там, где нужен, потому что «просрочено» это про старые работы, а курсор
 * отдаёт свежие. Год истории у преподавателя с двенадцатью учениками это около
 * семисот работ.
 *
 * Значения повторяют смысл `homeworkState` и `homeworkOverdue`, и предикат в
 * SQL обязан оставаться их зеркалом (разбор в `listHomeworkPage`).
 */
export const HomeworkStateFilter = z.enum(["all", "not_done", "overdue", "done"]);
export type HomeworkStateFilter = z.infer<typeof HomeworkStateFilter>;

export const HomeworkListResponse = z.object({
  homeworks: z.array(HomeworkSummary),
  /**
   * Корзина. Заполняется только преподавателю, ученику всегда пусто.
   *
   * ⚠️ С 18.08.2026 (шаг А4, раздел 7 фазы 2) заполняется ТОЛЬКО в ответе на
   * `?trash=1`. В обычном ответе списка его больше нет: корзина уехала в
   * собственный запрос со своим курсором, а поле в обычном ответе жило ровно
   * одну выкатку, потому что объявленное обязательным оно уронило бы старый
   * бандл на разборе, а не показало бы пустую корзину.
   *
   * Отсюда и `optional()`: у одной ручки два ответа, и в одном из них поля нет.
   * Читатель обязан писать `trash ?? []`, а не рассчитывать на массив.
   */
  trash: z.array(HomeworkSummary).optional(),
  /**
   * Курсор следующей страницы, `null` означает «дальше ничего нет». Клиент
   * возвращает строку как есть и не разбирает её.
   *
   * Отсутствие поля означает «сервер старый»: web едет после API, но вкладка,
   * открытая через окно выкатки, не должна падать на разборе.
   */
  nextCursor: z.string().nullable().optional(),
  /**
   * Сколько работ в каждом состоянии, посчитано по ВСЕЙ выборке, а не по
   * приехавшей странице (5.2 фазы 2, расширение от 18.08.2026).
   *
   * ⚠️ Считать это на клиенте нельзя, и не потому, что дорого. Страница
   * курсорная (25 строк, сверху свежие), а просрочка живёт в старых работах:
   * клиентский счётчик систематически показывал бы ноль ровно там, где нужен.
   *
   * Едет только преподавателю и только на первой странице (без `cursor`):
   * счётчики стоят у фильтра, фильтр не меняется от долистывания, а
   * `COUNT(*) FILTER` на каждой странице это агрегат по всей истории пары ради
   * чисел, которые уже показаны.
   *
   * Разрез по ученику учитывается: числа обязаны отвечать на тот же вопрос,
   * что и список под ними, иначе чип «Просрочено · 7» рядом со списком одного
   * ученика соврёт про шестерых чужих.
   */
  counts: z
    .object({
      all: z.number().int().nonnegative(),
      notDone: z.number().int().nonnegative(),
      overdue: z.number().int().nonnegative(),
      done: z.number().int().nonnegative(),
    })
    .optional(),
});
export type HomeworkListResponse = z.infer<typeof HomeworkListResponse>;

/**
 * Сколько работ ученику задали и он их ещё не открывал. Ответ
 * `GET /v1/homework/unopened`, источник значка над разделом «Домашка» и полосы
 * «вам задали домашнее задание» на расписании.
 *
 * ⚠️ Это НЕ список дел, а извещение, и предикат выбран под это. Считаются
 * работы БЕЗ отметки об открытии, а не невыполненные: значок гаснет, когда
 * человек работу открыл, даже если не решил ни одного задания. Иначе он висел
 * бы всю неделю до срока и перестал бы что-либо значить ровно к тому дню,
 * когда задали следующую.
 *
 * ⚠️ Транспортом уведомлений это не является и раздела 4 CLAUDE.md не
 * касается: наружу ничего не отправляется, число показывается тому, кто и так
 * открыл кабинет. Проект домашки обещал «заданное видно при любом входе», и до
 * сих пор это было правдой только для того, кто сам догадался зайти в раздел.
 *
 * У преподавателя число всегда ноль: предикат по `student_user_id`. Поэтому
 * клиенту не нужна ветка по роли, ему нужен только сам счёт.
 */
export const UnopenedHomeworkResponse = z.object({
  count: z.number().int().nonnegative(),
});
export type UnopenedHomeworkResponse = z.infer<typeof UnopenedHomeworkResponse>;

/**
 * Идентификатор последней раздачи в уже приехавшем списке (5.2 фазы 2).
 *
 * Отдельной ручки `?latest=1` нет намеренно: список отсортирован по времени
 * создания вниз, значит `batchId` первой строки с непустым полем это и есть
 * последняя раздача, и ручка ради значения, лежащего в первой строке ответа,
 * была бы третьим местом, знающим про порядок.
 *
 * `null` означает «раздач в списке нет», и переключатель в этом случае обязан
 * быть ВЫКЛЮЧЕН с подписью «выдач ещё не было»: пустой список читается как
 * «работ нет», выключенный переключатель как «нечего фильтровать».
 */
export function latestBatchId(
  rows: readonly { batchId?: string | null | undefined }[],
): string | null {
  for (const row of rows) {
    const batchId = row.batchId ?? null;
    if (batchId !== null) return batchId;
  }
  return null;
}

export const HomeworkExerciseView = z.object({
  elementId: z.string().min(1),
  position: z.number().int().nonnegative(),
  /** То, что видит ученик. Ответа внутри нет по построению типа. */
  element: ExerciseElement,
  /** Не null означает «задание снято преподавателем» (6.4 проекта). */
  voidedAt: z.number().int().nullable(),
  /**
   * Откуда взято задание. ⚠️ Едет ТОЛЬКО роли преподавателя (правило сборки в
   * одном месте, как у `planTitle`): у генераторов внутри мисконцепции, а они
   * выдают верный ответ методом исключения (3.3 проекта). Схема разбора живёт
   * в `@edu/shared/checking`, здесь поле нарочно неразобранное, чтобы не тащить
   * подпуть в стартовый чанк.
   */
  origin: z.unknown().optional(),
});
export type HomeworkExerciseView = z.infer<typeof HomeworkExerciseView>;

export const HomeworkCard = HomeworkSummary.extend({
  exercises: z.array(HomeworkExerciseView),
  /**
   * Сколько живых работ в раздаче, которой создана эта работа (4.6 фазы 2).
   *
   * Нужно ровно одному действию: пакетному аннулированию, кнопка которого
   * обязана называть число ДО нажатия («снять во всех 12 работах»), а не после.
   * Число считается тем же предикатом, каким пакетная ручка потом обновляет
   * строки (та же раздача, тот же преподаватель, корзина исключена), иначе
   * кнопка обещала бы одно, а снималось бы другое.
   *
   * ⚠️ Едет ТОЛЬКО роли преподавателя и только в карточке, тем же единственным
   * гейтом, что `batchId`: ученику метка раздачи не уезжает вовсе, а размер
   * раздачи это ещё и «скольким ещё задали то же самое». В строке списка поля
   * нет намеренно: там оно стоило бы агрегата на каждую строку ради кнопки,
   * которой на списке нет.
   *
   * Отсутствие означает «работа не из раздачи» либо «смотрит ученик».
   */
  batchSize: z.number().int().positive().optional(),
  /**
   * Фотографии решения, приложенные учеником ко ВСЕЙ этой работе
   * (`docs/homework-photo-design.md`). Порядок по времени добавления.
   *
   * ⚠️ **В корне карточки, а не у задания** (решение владельца 05.09.2026,
   * редакция 2 проекта). Фотография тетради отвечает на вопрос «как он это
   * решал», и раскладывать её по заданиям берётся сам преподаватель. Список у
   * задания жил здесь сутки и снят вместе с колонкой `element_id`.
   *
   * Едет обеим ролям, и это не оплошность: ученик видит, что он приложил, а
   * преподаватель то же самое плюс свою отметку просмотра. Разница между
   * половинами ровно одна и живёт внутри строки, а не здесь (`viewedAt`).
   *
   * Отдельной ручки списка нет намеренно (раздел 5 проекта): оба экрана и так
   * запрашивают карточку, а второй запрос на канале 128 кбит/с это лишний круг
   * ради данных, которые уже едут.
   */
  photos: z.array(HomeworkPhoto).default([]),
  /**
   * Сколько фотографий решения можно приложить к этой РАБОТЕ
   * (раздел 3 `docs/homework-photo-design.md`).
   *
   * ⚠️ **Число приезжает с сервера, а не живёт в бандле.** Однажды оно станет
   * зависеть от преподавателя, и зашитое в веб оно потребовало бы выкатки двух
   * приложений вместо одного, а между ними ученик получал бы отказ на кнопку,
   * которая ему разрешена.
   *
   * ⚠️ Умолчание `0`, а не число: оно означает «прикладывать нельзя», и это
   * ровно то, что должен показать новый бандл, попавший на старый сервер.
   * Настоящее умолчание живёт на сервере, в `photoLimitFor`.
   */
  photoLimit: z.number().int().nonnegative().default(0),
  /**
   * Текст задания (1.1 `docs/homework-scratch-design.md`). `null` означает
   * «текста нет»; умолчание держит старый сервер.
   */
  taskText: z.string().nullable().default(null),
  /**
   * Ответ ученика текстом (1.8 проекта), обеим ролям: ученик видит, что
   * отправил, преподаватель читает. `null` при `counters.answerAt` не null
   * означает «стёрт по сроку хранения» (`homeworkAnswerErased`). Момент
   * отправки живёт в `counters.answerAt`, а не здесь: второе место с тем же
   * числом однажды разошлось бы с первым.
   */
  answerText: z.string().nullable().default(null),
  /**
   * Последний ВОЗВРАЩЁННЫЙ разбор ответа текстом (1.9 проекта): пометки,
   * итог, комментарий и копия проверенного текста. Обеим ролям: ученик видит
   * свою проверку, преподаватель то же самое. Черновик сюда не едет никаким
   * путём: выборка с `returned_at IS NOT NULL` в `WHERE`. `null`: не
   * возвращали; умолчание держит старый сервер.
   */
  answerReview: ReturnedAnswerReview.nullable().default(null),
  /**
   * Картинки материала в порядке показа, со ссылками, подписанными для того,
   * кто смотрит (1.3 проекта). Обеим ролям одинаково.
   */
  material: z.array(HomeworkMaterialImage).default([]),
  /**
   * Аудио и видео материала (`docs/homework-media-plan.md`, Р5): позиции из той
   * же шкалы, что у `material`, клиент сливает их по `position`. `name` у
   * элементов только у роли преподавателя. Умолчание держит старый сервер.
   */
  media: z.array(HomeworkMaterialMedia).default([]),
  /**
   * Ведётся ли у домашки отметка нажатия на ссылки текста задания
   * (`docs/homework-media-plan.md`, Р8): выдана после выкатки и не старше
   * срока `HOMEWORK_LINK_OPEN_RETENTION_DAYS` от создания. Обеим ролям: это
   * свойство домашки, а не метка про ученика. По нему ученику показывается
   * строка раскрытия, а преподавателю блок отметок. Умолчание держит старый
   * сервер.
   */
  trackLinkOpens: z.boolean().default(false),
  /**
   * Первые нажатия на ссылки текста задания (Р10). ⚠️ **Только роли
   * преподавателя**: у ученика ключа нет вовсе, по образцу `reviewField`. Есть
   * только при `trackLinkOpens`. Адреса, на которые не нажимали, здесь не
   * перечисляются: список ссылок клиент берёт `linkHrefs(taskText)`.
   */
  linkOpens: z.array(HomeworkLinkOpen).optional(),
});
export type HomeworkCard = z.infer<typeof HomeworkCard>;

/**
 * Ближайшее не отменённое занятие пары: источник предзаполнения срока (4.1
 * проекта). Ручка существует потому, что раскрытие серий «по правилу» дало бы
 * отменённое занятие, а форма не должна знать про исключения и переносы.
 */
export const NextOccurrenceResponse = z.object({
  /** Момент начала занятия. `null` означает «расписания у пары нет». */
  at: z.number().int().nullable(),
  /** Зона серии, из которой занятие. Подпись при расхождении зон. */
  timeZone: z.string().nullable(),
});
export type NextOccurrenceResponse = z.infer<typeof NextOccurrenceResponse>;

/* ─────────────── вычисление состояния: факты, а не колонка ─────────────── */

/**
 * Основная ось автомата (раздел 5 проекта). Хранимого статуса нет: урок
 * `status != 'live'` уже наступил на эти грабли (`lessons.ts:284-307`), и
 * домашка их не повторяет.
 *
 * «Выполнена» это «по каждому заданию есть зачётная попытка», а НЕ «каждое
 * решено верно»: выполненность и успешность это разные оси, и требовать
 * верности значило бы, что честно не справившийся ученик вечно «не сделал».
 *
 * ⚠️ **У работы без заданий правило другое** (решение владельца 26.09.2026,
 * 1.5 `docs/homework-scratch-design.md`): выполнена, если ученик нажал
 * «Сделал(а)» или приложил хотя бы одно фото, то есть есть `studentDoneAt`.
 * «Без заданий» это ноль НЕаннулированных, поэтому сюда же попадает смешанная
 * работа, у которой сняли все задания (иначе она вечно просрочена).
 *
 * ⚠️ Правило живёт в семи местах (SQL `DONE` в `repo/homework-list.ts`, эта
 * функция и её вызовы, `homeworkOverdue`), и разъехаться им нельзя. Поле
 * `studentDoneAt` ОБЯЗАТЕЛЬНОЕ в аргументе ровно поэтому: забытое, оно молча
 * вернуло бы старое правило, а обязательное находит компилятор.
 */
export type HomeworkState = "not_opened" | "in_progress" | "done";

export function homeworkState(args: {
  firstOpenedAt: number | null;
  counters: { total: number; attempted: number; studentDoneAt: number | null };
}): HomeworkState {
  const { total, attempted, studentDoneAt } = args.counters;
  if (total > 0 ? attempted >= total : studentDoneAt !== null) return "done";
  return args.firstOpenedAt === null ? "not_opened" : "in_progress";
}

/**
 * Просрочка это вычисленный флаг, а не состояние: домашка не выполнена и срок
 * прошёл (4.3 проекта). Хранить нечего, переводить некому, cron не нужен.
 * Клиентские часы участвуют только в подписи, где минуты расхождения безобидны.
 */
export function homeworkOverdue(args: {
  dueAt: number | null;
  state: HomeworkState;
  now: number;
}): boolean {
  return args.dueAt !== null && args.state !== "done" && args.dueAt < args.now;
}

/**
 * Пометки попытки «после срока» и «после раскрытия» (4.3 и 6.2 проекта).
 *
 * Считает их СЕРВЕР при сборке ответа, а не клиент: у клиента преподавателя
 * есть `revealedAt`, но правило в двух местах однажды разошлось бы. Функция
 * одна на сводку и историю.
 */
export function attemptMarks(args: {
  attemptAt: number;
  dueAt: number | null;
  revealedAt: number | null;
  /**
   * Когда по этому заданию впервые вернули проверку перевода, `null` если не
   * возвращали. Попытка после возврата это пересдача, которую попросил сам
   * преподаватель, и «после срока» на ней не ставится: та же укоризна, что
   * снята с «после раскрытия» у перевода (2.10 docs/translate-review-design.md).
   */
  reviewReturnedAt?: number | null;
}): { afterDue: boolean; afterReveal: boolean } {
  const resubmission =
    args.reviewReturnedAt !== undefined &&
    args.reviewReturnedAt !== null &&
    args.attemptAt > args.reviewReturnedAt;
  return {
    afterDue: !resubmission && args.dueAt !== null && args.attemptAt > args.dueAt,
    afterReveal: args.revealedAt !== null && args.attemptAt > args.revealedAt,
  };
}
